<template>
  <div class="min-h-screen">
    <!-- Hero Section -->
    <section
      class="relative min-h-[900px] flex items-center justify-center overflow-hidden bg-gradient-to-r from-[#1a2a4d] to-[#233862]"
    >
      <!-- Background Image with Overlay -->
      <div class="absolute inset-0 z-0">
        <div class="absolute inset-0 bg-gradient-to-b from-[#233862]/90 to-[#1a2a4d]/90"></div>
        <picture>
          <!-- Responsive image sources -->
          <source
            media="(min-width: 1024px)"
            srcset="@/assets/santorini-lg.webp"
            type="image/webp"
          />
          <source
            media="(min-width: 640px)"
            srcset="@/assets/santorini-md.webp"
            type="image/webp"
          />
          <img
            src="@/assets/santorini-sm.webp"
            srcset="@/assets/santorini-sm.webp"
            alt="Greek Islands"
            class="w-full h-full object-cover opacity-20 scale-110 animate-slow-zoom"
            fetchpriority="high"
            loading="eager"
            decoding="async"
            style="background-color: #1a2a4d; background-image: url('data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/4gHYSUNDX1BST0ZJTEUAAQEAAAHIAAAAAAQwAABtbnRyUkdCIFhZWiAH4AABAAEAAAAAAABhY3NwAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAQAA9tYAAQAAAADTLQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAlkZXNjAAAA8AAAACRyWFlaAAABFAAAABRnWFlaAAABKAAAABRiWFlaAAABPAAAABR3dHB0AAABUAAAABRyVFJDAAABZAAAAChnVFJDAAABZAAAAChiVFJDAAABZAAAAChjcHJ0AAABjAAAADxtbHVjAAAAAAAAAAEAAAAMZW5VUwAAAAgAAAAcAHMAUgBHAEJYWVogAAAAAAAAb6IAADj1AAADkFhZWiAAAAAAAABimQAAt4UAABjaWFlaIAAAAAAAACSgAAAPhAAAts9YWVogAAAAAAAA9tYAAQAAAADTLXBhcmEAAAAAAAQAAAACZmYAAPKnAAANWQAAE9AAAApbAAAAAAAAAABtbHVjAAAAAAAAAAEAAAAMZW5VUwAAACAAAAAcAEcAbwBvAGcAbABlACAASQBuAGMALgAgADIAMAAxADb/2wBDABQODxIPDRQSEBIXFRQdHx4eHRoaHSQtJSAyVC08MTY3LjIyOUFTRjo/QjYyPUNhYVdbX2JfYDliaXN6e4GBgf/2wBDARUXFyAcIHQ4eGQ5DQT1GRkZGRkZGRkZGRkZGRkZGRkZGRkZGRkZGRkZGRkZGRkZGRkZGRkZGRkZGRkZGRkb/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAb/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k=');"
          />
        </picture>
      </div>

      <!-- Content -->
      <div class="relative z-10 container mx-auto">
        <div class="max-w-4xl mx-auto">
          <div class="text-center mb-8 space-y-4">
            <h1 class="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {{
                currentLanguage === 'en'
                  ? 'Aktoploika: Book Ferry Tickets Online Greece'
                  : 'Aktoploika: Κράτηση Ακτοπλοϊκών Εισιτηρίων Online'
              }}
            </h1>
            <p class="text-xl text-white/80 max-w-2xl mx-auto">
              {{
                currentLanguage === 'en'
                  ? 'Book your ferry tickets to over 90 destinations in Greece'
                  : 'Κλείστε τα ακτοπλοϊκά σας εισιτήρια για πάνω από 90 προορισμούς στην Ελλάδα'
              }}
            </p>
          </div>

          <!-- Ferryhopper Widget with floating effect -->
          <div class="transform">
            <iframe
              width="100%"
              height="450"
              scrolling="no"
              frameborder="0"
              :src="
                'https://www.ferryhopper.com/' +
                (currentLanguage === 'en' ? 'en' : 'el') +
                '/embed/simple?aff_uid=webee&color=ff5f00'
              "
              class="mx-auto rounded-xl"
              title="Ferryhopper Booking Widget"
            ></iframe>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <section class="py-16 bg-gradient-to-b from-white to-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mt-2">
          <h2 class="text-3xl font-bold text-[#233862] mb-4">
            {{
              currentLanguage === 'en'
                ? 'Ferry Tickets Online Greece'
                : 'Ακτοπλοϊκά Εισιτήρια Online'
            }}
          </h2>
          <p class="text-lg text-gray-600 max-w-4xl mx-auto">
            {{
              currentLanguage === 'en'
                ? "Discover the ease of booking ferry tickets online with Aktoploika. Our platform offers a seamless experience for planning your Greek island adventures. Whether you're exploring the Cyclades or venturing to Crete, Aktoploika ensures you find the best routes and schedules with just a few clicks. Ferry tickets for Mykonos, Santorini, Crete, Ios, and many more destinations."
                : 'Ανακαλύψτε την ευκολία της online κράτησης ακτοπλοϊκών εισιτηρίων με την Aktoploika. Η πλατφόρμα μας προσφέρει μια απρόσκοπτη εμπειρία για το σχεδιασμό των περιπετειών σας στα ελληνικά νησιά. Είτε εξερευνείτε τις Κυκλάδες είτε ταξιδεύετε στην Κρήτη, η Aktoploika σας εξασφαλίζει τα καλύτερα δρομολόγια με λίγα μόνο κλικ. Ακτοπλοϊκά εισιτήρια για Μύκονο, Σαντορίνη, Κρήτη, Ίο και πολλούς ακόμη προορισμούς.'
            }}
          </p>
        </div>
      </div>
    </section>

    <!-- Popular Routes -->
    <section class="py-12 bg-gradient-to-b from-white to-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-8">
          <h2 class="text-3xl font-bold text-[#233862] mb-2">
            {{ currentLanguage === 'en' ? 'Popular Destinations' : 'Δημοφιλείς Προορισμοί' }}
          </h2>
          <p class="text-gray-600 max-w-2xl mx-auto">
            {{
              currentLanguage === 'en'
                ? 'Discover the most iconic Greek islands'
                : 'Ανακαλύψτε τα πιο εμβληματικά ελληνικά νησιά'
            }}
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <router-link
            :to="{ name: getRouteName('destination'), params: { id: 'mykonos' } }"
            class="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow"
          >
            <div class="relative h-48">
              <img
                src="@/assets/images/destinations/mykonos.webp"
                alt="Mykonos"
                class="w-full h-full object-cover"
              />
              <div
                class="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"
              ></div>
            </div>
            <div class="p-4">
              <h3
                class="text-xl font-semibold text-[#233862] mb-2 group-hover:text-[#3D5A98] transition-colors"
              >
                <span class="mr-2">🏖️</span>
                {{ currentLanguage === 'en' ? 'Mykonos' : 'Μύκονος' }}
              </h3>
              <p class="text-gray-600 text-sm">
                {{
                  currentLanguage === 'en'
                    ? 'Discover the cosmopolitan island known for its vibrant nightlife and stunning beaches'
                    : 'Ανακαλύψτε το κοσμοπολίτικο νησί που φημίζεται για την έντονη νυχτερινή ζωή και τις εντυπωσιακές παραλίες'
                }}
              </p>
            </div>
          </router-link>

          <router-link
            :to="{ name: getRouteName('destination'), params: { id: 'santorini' } }"
            class="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow"
          >
            <div class="relative h-48">
              <img
                src="@/assets/images/destinations/santorini.webp"
                alt="Santorini"
                class="w-full h-full object-cover"
              />
              <div
                class="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"
              ></div>
            </div>
            <div class="p-4">
              <h3
                class="text-xl font-semibold text-[#233862] mb-2 group-hover:text-[#3D5A98] transition-colors"
              >
                <span class="mr-2">🌅</span>
                {{ currentLanguage === 'en' ? 'Santorini' : 'Σαντορίνη' }}
              </h3>
              <p class="text-gray-600 text-sm">
                {{
                  currentLanguage === 'en'
                    ? 'Experience the world-famous sunsets and unique volcanic landscapes'
                    : 'Ζήστε τα παγκοσμίως διάσημα ηλιοβασιλέματα και τα μοναδικά ηφαιστειακά τοπία'
                }}
              </p>
            </div>
          </router-link>

          <router-link
            :to="{ name: getRouteName('destination'), params: { id: 'crete' } }"
            class="group bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow"
          >
            <div class="relative h-48">
              <img
                src="@/assets/images/destinations/crete.webp"
                alt="Crete"
                class="w-full h-full object-cover"
              />
              <div
                class="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors"
              ></div>
            </div>
            <div class="p-4">
              <h3
                class="text-xl font-semibold text-[#233862] mb-2 group-hover:text-[#3D5A98] transition-colors"
              >
                <span class="mr-2">🏛️</span>
                {{ currentLanguage === 'en' ? 'Crete' : 'Κρήτη' }}
              </h3>
              <p class="text-gray-600 text-sm">
                {{
                  currentLanguage === 'en'
                    ? "Explore Greece's largest island, rich in history, culture, and natural beauty"
                    : 'Εξερευνήστε το μεγαλύτερο νησί της Ελλάδας, πλούσιο σε ιστορία, πολιτισμό και φυσική ομορφιά'
                }}
              </p>
            </div>
          </router-link>
        </div>

        <div class="text-center mt-12">
          <router-link
            :to="{ name: getRouteName('destinationsmulti') }"
            class="inline-flex items-center text-[#233862] hover:text-[#3D5A98] font-medium"
          >
            {{
              currentLanguage === 'en' ? 'View All Destinations' : 'Δείτε Όλους τους Προορισμούς'
            }}
            <svg class="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Why Book with Us -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <h2 class="text-2xl font-bold text-[#233862] mb-12 text-center">
          {{ currentLanguage === 'en' ? 'Why Book with Us' : 'Γιατί να Κάνετε Κράτηση Μαζί μας' }}
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="text-center">
            <div class="flex justify-center mb-4">
              <svg
                class="w-12 h-12 text-[#FF5F00]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-[#233862] mb-2">
              {{ currentLanguage === 'en' ? 'Fast & Easy Booking' : 'Γρήγορη & Εύκολη Κράτηση' }}
            </h3>
            <p class="text-gray-600">
              {{
                currentLanguage === 'en'
                  ? 'Book your ferry tickets in minutes with our user-friendly platform'
                  : 'Κλείστε τα ακτοπλοϊκά σας εισιτήρια σε λίγα λεπτά με την εύχρηστη πλατφόρμα μας'
              }}
            </p>
          </div>

          <div class="text-center">
            <div class="flex justify-center mb-4">
              <svg
                class="w-12 h-12 text-[#FF5F00]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-[#233862] mb-2">
              {{ currentLanguage === 'en' ? 'Secure Payments' : 'Ασφαλείς Πληρωμές' }}
            </h3>
            <p class="text-gray-600">
              {{
                currentLanguage === 'en'
                  ? 'Your transactions are protected with the latest security measures'
                  : 'Οι συναλλαγές σας προστατεύονται με τα πιο σύγχρονα μέτρα ασφαλείας'
              }}
            </p>
          </div>

          <div class="text-center">
            <div class="flex justify-center mb-4">
              <svg
                class="w-12 h-12 text-[#FF5F00]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-[#233862] mb-2">
              {{
                currentLanguage === 'en'
                  ? 'Support from FerryHopper'
                  : 'Υποστήριξη απο την FerryHopper'
              }}
            </h3>
            <p class="text-gray-600">
              {{
                currentLanguage === 'en'
                  ? 'Our customer service team is always here to help you'
                  : 'Η ομάδα εξυπηρέτησης πελατών μας είναι πάντα εδώ για να σας βοηθήσει'
              }}
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { onMounted } from 'vue'
import { updateMetaTags, addStructuredData, getWebsiteStructuredData } from '@/utils/seo'

const route = useRoute()

// Compute current language from route
const currentLanguage = computed(() => {
  return route.path.includes('/el/') ? 'el' : 'en'
})

// Compute route names with language prefix
const getRouteName = (baseName) => {
  return currentLanguage.value === 'el' ? `el${baseName}` : baseName
}

onMounted(() => {
  // Update meta tags based on language
  updateMetaTags({
    title:
      currentLanguage.value === 'el'
        ? 'Aktoploika | Κράτηση Ακτοπλοϊκών Εισιτηρίων Online'
        : 'Aktoploika | Book Ferry Tickets Online Greece',
    description:
      currentLanguage.value === 'el'
        ? 'Κλείστε ακτοπλοϊκά εισιτήρια online για όλους τους προορισμούς στην Ελλάδα. Βρείτε τα καλύτερα δρομολόγια για Μύκονο, Σαντορίνη, Κρήτη και άλλα νησιά.'
        : 'Book ferry tickets online for all destinations in Greece. Find the best routes to Mykonos, Santorini, Crete, and other islands.',
    keywords:
      currentLanguage.value === 'el'
        ? 'ακτοπλοϊκά εισιτήρια, κράτηση εισιτηρίων, ελληνικά νησιά, ακτοπλοϊκά δρομολόγια'
        : 'ferry tickets, book tickets, greek islands, ferry routes',
  })

  // Add structured data
  addStructuredData(getWebsiteStructuredData())
})
</script>

<style>
.animate-float {
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
}

.animate-slow-zoom {
  animation: slow-zoom 20s ease-in-out infinite;
}

@keyframes slow-zoom {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
</style>
